/** @jsxRuntime classic */
/** @jsx jsx */
import {
  GetChangeDto,
  UpdateChangeDtoStatusEnum,
} from '@codefluegel/zeta-change-typescript-client';
import { jsx } from '@emotion/react';
import {
  Box,
  InputAdornment,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import { observer } from 'mobx-react-lite';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NumericFormat } from 'react-number-format';
import { useChangeStore } from '../../store/changeStore';

export type CalculationResultPageProps = {
  handleChangeProcess: (fieldName: string, value: number) => void;
  editable: boolean;
  materialValue: number;
  change: GetChangeDto | null;
};

const CalculationResultPage = observer(
  ({
    handleChangeProcess,
    materialValue,
    editable,
    change,
  }: CalculationResultPageProps) => {
    const { t } = useTranslation();

    const [projectDiscount, setProjectDiscount] = useState(
      Number(change?.projectDiscount || 0),
    );

    const updateChange = (fieldName: string, value: number) => {
      if (value) {
        handleChangeProcess(fieldName, value);
      }
    };

    const changestore = useChangeStore();

    const {
      calculatedHoursCosts,
      calculatedMaterialCosts,
      calculatedServiceCosts,
    } = changestore.calculationStore;

    const lang = localStorage.getItem('i18nextLng') || 'de';
    const thousandSeparator = lang !== 'de' ? ',' : '.';
    const decimalSeparator = lang !== 'de' ? '.' : ',';

    const labelName = t('calc.calcValue');
    return (
      <Paper sx={{ p: 2, mt: 2, mb: 2, borderRadius: 2 }}>
        <Typography variant="h4" sx={{ mb: 2 }}>
          Finale Sales Werte
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <NumericFormat
            name="calcValue"
            label={labelName}
            customInput={TextField}
            value={calculatedHoursCosts}
            thousandSeparator={thousandSeparator}
            decimalSeparator={decimalSeparator}
            variant="standard"
            required
            disabled
            InputProps={{
              sx: {
                pl: 2,
                pb: 0.5,
                fontSize: 14,
                fontWeight: 300,
              },
            }}
            InputLabelProps={{
              sx: {
                pl: 2.5,
                fontWeight: 300,
                color: 'text.primary',
              },
            }}
          />

          <NumericFormat
            label={t('costs.materialValue')}
            name="materialValue"
            value={calculatedMaterialCosts + calculatedServiceCosts}
            thousandSeparator={thousandSeparator}
            decimalSeparator={decimalSeparator}
            customInput={TextField}
            disabled
            variant="standard"
            required
            InputProps={{
              inputMode: 'numeric',
              sx: {
                pl: 2,
                pb: 0.5,
                fontSize: 14,
                fontWeight: 300,
              },
            }}
            InputLabelProps={{
              sx: {
                pl: 2.5,
                fontWeight: 300,
                color: 'text.primary',
              },
            }}
          />

          <NumericFormat
            name="projectDiscount"
            label={t('costs.discountQuotationValue')}
            value={projectDiscount}
            thousandSeparator={thousandSeparator}
            decimalSeparator={decimalSeparator}
            customInput={TextField}
            onValueChange={values => setProjectDiscount(values.floatValue ?? 0)}
            onBlur={() => updateChange('projectDiscount', projectDiscount)}
            variant="standard"
            required
            disabled={
              !editable ||
              !(
                change?.status === UpdateChangeDtoStatusEnum.EvalAccepted ||
                change?.status === UpdateChangeDtoStatusEnum.EvalCalcRejected
              )
            }
            InputProps={{
              sx: {
                pl: 2,
                pb: 0.5,
                fontSize: 14,
                fontWeight: 300,
              },
            }}
            InputLabelProps={{
              sx: {
                pl: 2.5,
                fontWeight: 300,
                color: 'text.primary',
              },
            }}
          />

          <NumericFormat
            customInput={TextField}
            variant="standard"
            readOnly
            value={
              Number(calculatedHoursCosts) +
              Number(calculatedServiceCosts) +
              Number(materialValue) -
              Number(projectDiscount)
            }
            thousandSeparator={thousandSeparator}
            decimalSeparator={decimalSeparator}
            InputLabelProps={{ sx: { display: 'none' } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Typography
                    variant="body2"
                    sx={{
                      mr: 2,
                      fontSize: 12,
                      fontWeight: 600,
                      color: 'text.primary',
                    }}
                  >
                    {t('costs.quotationValue')}
                  </Typography>
                </InputAdornment>
              ),
              disableUnderline: true,
              sx: {
                pl: 2.5,
                fontSize: 14,
                fontWeight: 300,
                color: 'text.primary',
              },
            }}
          />
        </Box>
      </Paper>
    );
  },
);

export default CalculationResultPage;
